import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ActionIcon,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconFileText,
  IconLogout,
  IconPencil,
  IconPlus,
  IconTrash,
  IconUser,
  IconX,
} from '@tabler/icons-react';

import { CustomerAddress } from '@interfaces/address.interface';

import { useGetCurrentCustomerQuery } from '@api/customer.api';
import {
  useDeleteCustomerAddressMutation,
  useGetCustomerAddressesQuery,
} from '@api/customerAddress.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useAuth from '@hooks/useAuth';

import UpdateCustomerBillingAddress, {
  UPDATE_CUSTOMER_BILLING_ADDRESS_MODAL_ID,
} from '@components/UpdateCustomerBillingAddress';
import UpsertCustomerAddress, {
  UPSERT_CUSTOMER_ADDRESS_MODAL_ID,
} from '@components/UpsertCustomerAddress';

import classes from './UserAccount.module.css';

const UserAccount = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();

  const [params, setParams] = useQueryParams();
  const page = useMemo(() => params.get('p'), [params]);

  const { data: customer } = useGetCurrentCustomerQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: addresses = [], isLoading: isLoadingAddresses } =
    useGetCustomerAddressesQuery();
  const [deleteCustomerAddress] = useDeleteCustomerAddressMutation();

  const handleEditProfileClick = () => {
    openModal({
      title: t('modals.editBillingAddress'),
      children: <UpdateCustomerBillingAddress customer={customer!} />,
      modalId: UPDATE_CUSTOMER_BILLING_ADDRESS_MODAL_ID,
    });
  };

  const handleAddAddressClick = () => {
    openModal({
      title: t('modals.addShippingAddress'),
      children: <UpsertCustomerAddress customer={customer} />,
      modalId: UPSERT_CUSTOMER_ADDRESS_MODAL_ID,
    });
  };

  const handleEditAddressClick = (address: CustomerAddress) => {
    openModal({
      title: t('modals.editShippingAddress'),
      children: (
        <UpsertCustomerAddress customerAddress={address} customer={customer} />
      ),
      modalId: UPSERT_CUSTOMER_ADDRESS_MODAL_ID,
    });
  };

  const handleDeleteAddressClick = async (id: number) => {
    openConfirmModal({
      title: t('userAccount.removeAddress'),
      children: t('userAccount.removeAddressConfirm'),
      labels: {
        cancel: t('userAccount.cancel'),
        confirm: t('userAccount.removeAddress'),
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteCustomerAddress(id).unwrap();

          showNotification({
            title: t('userAccount.addressRemoved'),
            message: t('userAccount.addressRemovedSuccess'),
          });
        } catch {
          showNotification({
            title: t('userAccount.error'),
            message: t('userAccount.cannotRemoveAddress'),
            color: 'red',
            icon: <IconX />,
          });
        }
      },
    });
  };

  return (
    <Grid gutter="xl">
      <Grid.Col span={{ base: 12, sm: 3 }}>
        <Paper p="lg" withBorder>
          <Stack gap={0}>
            <button
              className={classes.linkButton}
              onClick={() => {
                params.set('p', 'info');
                setParams(params);
              }}
            >
              <IconUser />
              <span>{t('userAccount.accountInfo')}</span>
            </button>
            {/* <button
              className={classes.linkButton}
              onClick={() => setActiveButton(2)}
            >
              <IconFileText />
              <span>Richieste di offerta</span>
            </button> */}
            <button className={classes.linkButton} onClick={logout}>
              <IconLogout color="red" />
              <span>{t('userAccount.logout')}</span>
            </button>
          </Stack>
        </Paper>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 9 }}>
        {page === 'offers' ? (
          <>
            <Group align="center" mb="lg">
              <IconFileText />
              <Title order={3}>{t('userAccount.offerRequests')}</Title>
            </Group>

            <Text fs="italic">{t('userAccount.noOfferRequests')}</Text>
          </>
        ) : (
          <>
            <Group align="center" mb="lg">
              <IconUser />
              <Title order={3}>{t('userAccount.accountInfo')}</Title>
            </Group>

            <Grid>
              <Grid.Col span={12}>
                <Text fw="bold" size="lg" mb="xs">
                  {t('userAccount.personalInfo')}
                </Text>

                <Group align="start" gap="xl">
                  <div>
                    <Group gap="xs">
                      <Text fs="italic">{t('userAccount.email')}:</Text>
                      <Text>{user?.email}</Text>
                    </Group>
                    <Group gap="xs">
                      <Text fs="italic">{t('userAccount.company')}:</Text>
                      <Text>{customer?.company}</Text>
                    </Group>
                    <Group gap="xs">
                      <Text fs="italic">{t('userAccount.vatNumber')}:</Text>
                      <Text>{customer?.vatNumber}</Text>
                    </Group>

                    {customer?.companyRoleName && (
                      <Group gap="xs">
                        <Text fs="italic">{t('userAccount.companyRole')}:</Text>
                        <Text>{customer?.companyRoleName}</Text>
                      </Group>
                    )}

                    {customer?.phone && (
                      <Group gap="xs">
                        <Text fs="italic">{t('userAccount.phone')}:</Text>
                        <Text>{customer?.phone}</Text>
                      </Group>
                    )}
                  </div>

                  <div>
                    <Group gap="xs">
                      <Text fs="italic">
                        {t('userAccount.interestMarkets')}:
                      </Text>
                      <Text>{customer?.allowedCategories.join(', ')}</Text>
                    </Group>

                    {customer && customer?.interestSector.length > 0 && (
                      <Group gap="xs">
                        <Text fs="italic">
                          {t('userAccount.interestSectors')}:
                        </Text>
                        <Text>{customer?.interestSector.join(', ')}</Text>
                      </Group>
                    )}

                    {customer && customer?.techniquesUsed.length > 0 && (
                      <Group gap="xs">
                        <Text fs="italic">
                          {t('userAccount.techniquesUsed')}:
                        </Text>
                        <Text>{customer?.techniquesUsed.join(', ')}</Text>
                      </Group>
                    )}

                    {customer && customer?.goodfellow.length > 0 && (
                      <Group gap="xs">
                        <Text fs="italic">{t('userAccount.goodfellow')}:</Text>
                        <Text>{customer?.goodfellow.join(', ')}</Text>
                      </Group>
                    )}

                    {customer && customer?.organizationType.length > 0 && (
                      <Group gap="xs">
                        <Text fs="italic">
                          {t('userAccount.organizationType')}:
                        </Text>
                        <Text>{customer?.organizationType.join(', ')}</Text>
                      </Group>
                    )}

                    {customer && customer?.disease.length > 0 && (
                      <Group gap="xs">
                        <Text fs="italic">{t('userAccount.diseases')}:</Text>
                        <Text>{customer?.disease.join(', ')}</Text>
                      </Group>
                    )}

                    {customer?.laboratory && (
                      <Group gap="xs">
                        <Text fs="italic">{t('userAccount.laboratory')}:</Text>
                        <Text>{customer?.laboratory}</Text>
                      </Group>
                    )}

                    {customer?.department && (
                      <Group gap="xs">
                        <Text fs="italic">{t('userAccount.department')}:</Text>
                        <Text>{customer?.department}</Text>
                      </Group>
                    )}

                    {customer?.additionalNotes && (
                      <Group gap="xs">
                        <Text fs="italic">{t('userAccount.notes')}:</Text>
                        <Text>{customer?.additionalNotes}</Text>
                      </Group>
                    )}
                  </div>
                </Group>

                <Divider mt="lg" />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                <Group align="center" mb="xs">
                  <Text fw="bold" size="lg">
                    {t('userAccount.billingAddress')}
                  </Text>
                  <Tooltip label={t('userAccount.editBillingAddress')}>
                    <ActionIcon onClick={handleEditProfileClick}>
                      <IconPencil />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Text fw="bold">
                  {user?.firstName} {user?.lastName}
                </Text>
                <Text>{customer?.address}</Text>
                <Text>
                  {customer?.postalCode} {customer?.city} ({customer?.province})
                </Text>
                <Text>{customer?.country}</Text>
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                <Group mb="xs">
                  <Text fw="bold" size="lg">
                    {t('userAccount.shippingAddresses')}
                  </Text>
                  <Tooltip label={t('userAccount.addShippingAddress')}>
                    <ActionIcon onClick={handleAddAddressClick}>
                      <IconPlus />
                    </ActionIcon>
                  </Tooltip>
                </Group>

                {isLoadingAddresses ? (
                  <Loader />
                ) : addresses.length === 0 ? (
                  <Text fs="italic">
                    {t('userAccount.noShippingAddresses')}
                  </Text>
                ) : (
                  addresses.map((address) => (
                    <Group key={address.id} justify="space-between">
                      <div>
                        <Text fw="bold">
                          {address.firstName} {address.lastName}
                        </Text>
                        <Text>{address.company}</Text>
                        <Text>{address.department}</Text>
                        <Text>{address.address}</Text>
                        <Text>
                          {address.postalCode} - {address.city} (
                          {address.province})
                        </Text>
                        {address.phone && address.phone.trim() !== '' && (
                          <Text>{address.phone}</Text>
                        )}
                      </div>
                      <Group gap="xs">
                        <Tooltip label={t('userAccount.edit')}>
                          <ActionIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditAddressClick(address);
                            }}
                          >
                            <IconPencil />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('userAccount.delete')}>
                          <ActionIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAddressClick(address.id);
                            }}
                            color="red"
                          >
                            <IconTrash />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  ))
                )}
              </Grid.Col>
            </Grid>
          </>
        )}
      </Grid.Col>
    </Grid>
  );
};

export default UserAccount;
